import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, AfterViewInit, ElementRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  CardapioProdutoDTO,
  CardapioItemCarrinhoDTO,
  CardapioGrupoModificadorDTO,
  CardapioOpcaoModificadorDTO,
  CardapioOpcaoSelecionadaDTO,
  CardapioVarianteDTO,
  CardapioEstabelecimentoDTO,
  CardapioFreteDTO,
} from '../../../../models/cardapio-digital/cardapio-digital.model';
import { formatarPreco as formatarPrecoUtil } from '../../utils/cardapio-preco.util';

@Component({
  selector: 'app-cardapio-produto-detalhe',
  templateUrl: './cardapio-produto-detalhe.component.html',
  styleUrls: ['./cardapio-produto-detalhe.component.scss'],
})
export class CardapioProdutoDetalheComponent implements OnChanges, AfterViewInit {

  @Input() produto: CardapioProdutoDTO | null = null;
  // Nome da categoria do produto (resolvido pelo componente-pai, pois o produto
  // do payload publico nao expoe a categoria diretamente). Exibido abaixo do preco.
  @Input() categoriaNome: string | null = null;
  @Input() idEmpresa = '';
  @Input() moedaSimbolo = 'BRL';
  @Input() moedaLocal = 'pt-BR';
  // Dados da LOJA usados na secao "Pagamento e entrega" (fonte: payload publico do cardapio).
  @Input() estabelecimento: CardapioEstabelecimentoDTO | null = null;
  @Input() freteConfig: CardapioFreteDTO | null = null;
  /** Quantidade deste produto ja adicionada ao carrinho (para limitar pelo estoque). */
  @Input() quantidadeNoCarrinho = 0;
  @Output() confirmar = new EventEmitter<CardapioItemCarrinhoDTO>();
  @Output() voltar = new EventEmitter<void>();
  @Output() compartilhar = new EventEmitter<void>();

  quantidade = 1;

  /**
   * `true` enquanto o lightbox da galeria de imagens esta aberto.
   *
   * Alimentado pelo (lightboxAbertoChange) do <app-item-imagens-carrossel>. So
   * serve para o CSS: com o lightbox aberto a coluna da imagem PRECISA deixar
   * de ser `position: sticky`, senao ela cria stacking context e prende o
   * overlay (position: fixed) do lightbox dentro dela.
   */
  lightboxAberto = false;

  // Variante selecionada (quando o produto tem variantes)
  varianteSelecionada: CardapioVarianteDTO | null = null;

  // Mapa de opcoes selecionadas por grupo: grupoId -> opcaoId[]
  opcoesSelecionadas: Map<number, number[]> = new Map();

  // Mapa de quantidades por opcao: "grupoId_opcaoId" -> quantidade
  quantidadesOpcoes: Map<string, number> = new Map();

  // Mapa de grupos abertos/fechados (colapsavel)
  gruposAbertos: Map<number, boolean> = new Map();

  constructor(private translate: TranslateService, private elementRef: ElementRef) {}

  ngAfterViewInit(): void {
    this.scrollToTop();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['produto'] && this.produto) {
      this.resetState();
      this.scrollToTop();
    }
    // Garante que a quantidade nunca exceda o maximo permitido pelo estoque
    // (considerando o que ja esta no carrinho).
    if (this.quantidade > this.maxQuantidade) {
      this.quantidade = Math.max(1, this.maxQuantidade);
    }
  }

  private scrollToTop(): void {
    // O scroll-ao-topo real e feito pelo componente PAI (scrollHostAoTopo), que
    // opera sobre o host scrollavel. Aqui NAO usamos scrollIntoView: ele alinhava
    // o topo do detalhe ao topo do container, ficando SOB a nav band sticky e
    // escondendo o nome do produto. Mantemos apenas fallbacks inofensivos.
    setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);
  }

  get temVariantes(): boolean {
    return !!this.produto?.temVariantes && !!this.produto.variantes && this.produto.variantes.length > 0;
  }

  /**
   * Chave de traducao do genero do produto (somente leitura no shop).
   * Retorna null apenas quando o produto nao tem genero definido.
   */
  get generoLabelKey(): string | null {
    const genero = this.produto?.genero;
    switch (genero) {
      case 'MASCULINO':
        return 'cardapio_digital.genero.masculino';
      case 'FEMININO':
        return 'cardapio_digital.genero.feminino';
      case 'UNISSEX':
        return 'cardapio_digital.genero.unissex';
      case 'PREFIRO_NAO_INFORMAR':
        return 'cardapio_digital.genero.prefiro_nao_informar';
      default:
        return null;
    }
  }

  selecionarVariante(variante: CardapioVarianteDTO): void {
    this.varianteSelecionada = variante;
  }

  isVarianteSelecionada(varianteId: number): boolean {
    return this.varianteSelecionada?.id === varianteId;
  }

  formatarPrecoVariante(variante: CardapioVarianteDTO): string {
    const preco = variante.precoPromocional ?? variante.preco;
    return this.formatarPreco(preco);
  }

  get precoUnitarioComOpcoes(): number {
    if (!this.produto) return 0;
    // Se o produto tem variantes, usa o preco da variante selecionada
    let precoBase: number;
    if (this.temVariantes && this.varianteSelecionada) {
      precoBase = this.varianteSelecionada.precoPromocional ?? this.varianteSelecionada.preco;
    } else {
      precoBase = this.produto.precoPromocional ?? this.produto.preco;
    }
    let adicional = 0;

    if (this.produto.modificadores) {
      this.produto.modificadores.forEach(grupo => {
        const selecionadas = this.opcoesSelecionadas.get(grupo.id) || [];
        grupo.opcoes.forEach(opcao => {
          if (selecionadas.includes(opcao.id)) {
            const qty = this.getQuantidadeOpcao(grupo.id, opcao.id);
            adicional += opcao.precoAdicional * qty;
          }
        });
      });
    }

    return precoBase + adicional;
  }

  get precoTotal(): number {
    return this.precoUnitarioComOpcoes * this.quantidade;
  }

  get precoTotalFormatado(): string {
    return this.formatarPreco(this.precoTotal);
  }

  get precoFormatado(): string {
    if (!this.produto) return '';
    return this.formatarPreco(this.produto.preco);
  }

  get precoPromocionalFormatado(): string | null {
    if (!this.produto?.precoPromocional) return null;
    return this.formatarPreco(this.produto.precoPromocional);
  }

  get temModificadores(): boolean {
    return !!this.produto?.modificadores && this.produto.modificadores.length > 0;
  }

  get podeConfirmar(): boolean {
    if (!this.produto) return false;
    // Respeitar o estoque disponivel (considerando o que ja esta no carrinho).
    if (this.quantidade > this.maxQuantidade || this.maxQuantidade <= 0) {
      return false;
    }
    // Verificar se variante foi selecionada quando o produto tem variantes
    if (this.temVariantes && !this.varianteSelecionada) {
      return false;
    }
    // Verificar se todos os grupos obrigatorios tem selecao minima
    if (this.produto.modificadores) {
      for (const grupo of this.produto.modificadores) {
        if (grupo.obrigatorio) {
          const totalSelecionado = this.getTotalSelecoesGrupo(grupo);
          if (totalSelecionado < grupo.minimoSelecoes) {
            return false;
          }
        }
      }
    }
    return true;
  }

  /**
   * Quantidade maxima que ainda pode ser adicionada deste item:
   * saldo disponivel MENOS o que ja esta no carrinho. Quando o produto nao
   * controla estoque (saldoDisponivel == null), usa o teto padrao de 99.
   */
  get maxQuantidade(): number {
    const saldo = this.produto?.saldoDisponivel;
    if (saldo == null) return 99;
    const restante = Math.floor(saldo) - this.quantidadeNoCarrinho;
    return Math.max(0, Math.min(99, restante));
  }

  /** Indica que nao ha estoque disponivel para adicionar (ja no limite). */
  get semEstoqueDisponivel(): boolean {
    return this.produto?.saldoDisponivel != null && this.maxQuantidade <= 0;
  }

  incrementar(): void {
    if (this.quantidade < this.maxQuantidade) {
      this.quantidade++;
    }
  }

  decrementar(): void {
    if (this.quantidade > 1) {
      this.quantidade--;
    }
  }

  toggleOpcao(grupo: CardapioGrupoModificadorDTO, opcao: CardapioOpcaoModificadorDTO): void {
    const selecionadas = this.opcoesSelecionadas.get(grupo.id) || [];
    const index = selecionadas.indexOf(opcao.id);

    if (index >= 0) {
      // Remover selecao
      selecionadas.splice(index, 1);
    } else {
      // Verificar se atingiu o maximo
      if (grupo.maximoSelecoes === 1) {
        // Radio: substituir selecao
        selecionadas.length = 0;
        selecionadas.push(opcao.id);
      } else if (selecionadas.length < grupo.maximoSelecoes) {
        selecionadas.push(opcao.id);
      }
    }

    this.opcoesSelecionadas.set(grupo.id, [...selecionadas]);
  }

  // Retorna o total de selecoes de um grupo (somando quantidades no multi-select)
  getTotalSelecoesGrupo(grupo: CardapioGrupoModificadorDTO): number {
    const selecionadas = this.opcoesSelecionadas.get(grupo.id) || [];
    if (grupo.maximoSelecoes === 1) {
      return selecionadas.length;
    }
    // Multi-select: somar quantidades
    let total = 0;
    for (const opcaoId of selecionadas) {
      total += this.getQuantidadeOpcao(grupo.id, opcaoId);
    }
    return total;
  }

  // Verificar se grupo esta completo (minimo atingido)
  isGrupoCompleto(grupo: CardapioGrupoModificadorDTO): boolean {
    if (!grupo.obrigatorio) return false;
    return this.getTotalSelecoesGrupo(grupo) >= grupo.minimoSelecoes;
  }

  // Obter quantidade de uma opcao multi-select
  getQuantidadeOpcao(grupoId: number, opcaoId: number): number {
    return this.quantidadesOpcoes.get(`${grupoId}_${opcaoId}`) || 1;
  }

  // Incrementar quantidade de uma opcao multi-select
  incrementarOpcao(grupo: CardapioGrupoModificadorDTO, opcao: CardapioOpcaoModificadorDTO, event: Event): void {
    event.stopPropagation();
    const selecionadas = this.opcoesSelecionadas.get(grupo.id) || [];

    if (!selecionadas.includes(opcao.id)) {
      // Primeira vez: adicionar opcao com qty 1
      if (this.getTotalSelecoesGrupo(grupo) < grupo.maximoSelecoes) {
        selecionadas.push(opcao.id);
        this.opcoesSelecionadas.set(grupo.id, [...selecionadas]);
        this.quantidadesOpcoes.set(`${grupo.id}_${opcao.id}`, 1);
      }
    } else {
      // Ja selecionada: incrementar qty (respeitando maximo do grupo)
      const qtyAtual = this.getQuantidadeOpcao(grupo.id, opcao.id);
      const totalGrupo = this.getTotalSelecoesGrupo(grupo);
      if (totalGrupo < grupo.maximoSelecoes) {
        this.quantidadesOpcoes.set(`${grupo.id}_${opcao.id}`, qtyAtual + 1);
      }
    }
  }

  // Decrementar quantidade de uma opcao multi-select
  decrementarOpcao(grupo: CardapioGrupoModificadorDTO, opcao: CardapioOpcaoModificadorDTO, event: Event): void {
    event.stopPropagation();
    const selecionadas = this.opcoesSelecionadas.get(grupo.id) || [];
    const chave = `${grupo.id}_${opcao.id}`;
    const qtyAtual = this.quantidadesOpcoes.get(chave) || 1;

    if (qtyAtual <= 1) {
      // Remover opcao
      const index = selecionadas.indexOf(opcao.id);
      if (index >= 0) {
        selecionadas.splice(index, 1);
        this.opcoesSelecionadas.set(grupo.id, [...selecionadas]);
      }
      this.quantidadesOpcoes.delete(chave);
    } else {
      this.quantidadesOpcoes.set(chave, qtyAtual - 1);
    }
  }

  // Toggle visibilidade do grupo
  toggleGrupo(grupoId: number): void {
    const aberto = this.gruposAbertos.get(grupoId);
    this.gruposAbertos.set(grupoId, !aberto);
  }

  // Verificar se grupo esta aberto (default: true)
  isGrupoAberto(grupoId: number): boolean {
    const estado = this.gruposAbertos.get(grupoId);
    return estado === undefined ? true : estado;
  }

  isOpcaoSelecionada(grupoId: number, opcaoId: number): boolean {
    const selecionadas = this.opcoesSelecionadas.get(grupoId) || [];
    return selecionadas.includes(opcaoId);
  }

  onConfirmar(): void {
    if (!this.produto || !this.podeConfirmar) return;

    // Montar lista de opcoes selecionadas (com quantidade para multi-select)
    const opcoesList: CardapioOpcaoSelecionadaDTO[] = [];
    if (this.produto.modificadores) {
      this.produto.modificadores.forEach(grupo => {
        const selecionadas = this.opcoesSelecionadas.get(grupo.id) || [];
        grupo.opcoes.forEach(opcao => {
          if (selecionadas.includes(opcao.id)) {
            const qty = this.getQuantidadeOpcao(grupo.id, opcao.id);
            opcoesList.push({
              opcaoId: opcao.id,
              nome: opcao.nome,
              precoAdicional: opcao.precoAdicional,
              quantidade: qty,
            });
          }
        });
      });
    }

    // Nome do item: inclui o nome da variante selecionada (ex: "Pizza - Grande")
    const nomeItem = this.varianteSelecionada
      ? `${this.produto.nome} - ${this.varianteSelecionada.nome}`
      : this.produto.nome;

    const item: CardapioItemCarrinhoDTO = {
      produtoId: this.produto.id,
      nome: nomeItem,
      quantidade: this.quantidade,
      precoUnitario: this.precoUnitarioComOpcoes,
      precoTotal: this.precoTotal,
      imagem: this.produto.foto || undefined,
      opcoesSelecionadas: opcoesList.length > 0 ? opcoesList : undefined,
      uid: this.gerarUid(),
    };

    this.confirmar.emit(item);
    this.resetState();
  }

  onVoltar(): void {
    this.resetState();
    this.voltar.emit();
  }

  onCompartilhar(): void {
    this.compartilhar.emit();
  }

  trackByGrupo(index: number, grupo: CardapioGrupoModificadorDTO): number {
    return grupo.id;
  }

  trackByOpcao(index: number, opcao: CardapioOpcaoModificadorDTO): number {
    return opcao.id;
  }

  formatarPrecoOpcao(valor: number): string {
    if (valor <= 0) return '';
    return '+' + this.formatarPreco(valor);
  }

  // Formata o saldo disponivel de forma amigavel, removendo zeros decimais
  // desnecessarios (ex: 12.000000 -> "12"; 12.5 -> "12,5" no locale da moeda).
  get saldoDisponivelFormatado(): string {
    if (this.produto?.saldoDisponivel == null) return '';
    return new Intl.NumberFormat(this.moedaLocal, {
      maximumFractionDigits: 3,
    }).format(this.produto.saldoDisponivel);
  }

  // ============================
  // Secao "Pagamento e entrega" (informacoes da LOJA)
  // ============================

  // Exibe a secao apenas quando ha alguma informacao real de pagamento ou entrega.
  get temInfoPagamentoEntrega(): boolean {
    return this.temFormasPagamento || this.temInfoEntrega;
  }

  // Formas de pagamento aceitas (genericas do sistema: mesmas opcoes do checkout).
  // O backend nao expoe formas de pagamento por loja, entao usamos as opcoes fixas
  // que o cliente realmente podera escolher ao finalizar o pedido.
  get formasPagamento(): string[] {
    return [
      this.translate.instant('cardapio_digital.checkout.pagamento_dinheiro'),
      this.translate.instant('cardapio_digital.checkout.pagamento_cartao'),
    ];
  }

  get temFormasPagamento(): boolean {
    return this.formasPagamento.length > 0;
  }

  // Tempo de entrega informado pela loja (minimo/maximo em minutos).
  get temTempoEntrega(): boolean {
    return this.estabelecimento?.tempoEntregaMinimo != null
      && this.estabelecimento?.tempoEntregaMaximo != null;
  }

  // Frete configurado e ativo para a loja.
  get temFreteAtivo(): boolean {
    return !!this.freteConfig && this.freteConfig.ativo;
  }

  // A loja faz entrega/delivery.
  get aceitaDelivery(): boolean {
    return !!this.estabelecimento?.aceitaDelivery;
  }

  // A loja permite retirada no local.
  get retiraNoLocal(): boolean {
    return !!this.estabelecimento?.retiraNoLocal;
  }

  // Ha alguma informacao de entrega para exibir (tempo, frete ou modalidades).
  get temInfoEntrega(): boolean {
    return this.temTempoEntrega || this.temFreteAtivo || this.aceitaDelivery || this.retiraNoLocal;
  }

  // Valor do frete formatado (valor fixo da loja).
  get freteFormatado(): string {
    if (!this.freteConfig) return '';
    return this.formatarPreco(this.freteConfig.valorFixo);
  }

  // Valor minimo para frete gratis formatado (quando configurado).
  get freteGratisMinimoFormatado(): string | null {
    const minimo = this.freteConfig?.valorMinimoFreteGratis;
    if (minimo == null) return null;
    return this.formatarPreco(minimo);
  }

  private resetState(): void {
    this.quantidade = 1;
    this.varianteSelecionada = null;
    this.opcoesSelecionadas = new Map();
    this.quantidadesOpcoes = new Map();
    this.gruposAbertos = new Map();
  }

  private formatarPreco(valor: number): string {
    return formatarPrecoUtil(valor, this.moedaSimbolo, this.moedaLocal);
  }

  private gerarUid(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  }
}
